import * as React from "react";
import {
    useFloating,
    autoUpdate,
    offset,
    flip,
    shift,
    arrow,
    useClick,
    useDismiss,
    useRole,
    useInteractions,
    useMergeRefs,
    Placement,
    FloatingPortal,
    FloatingFocusManager,
    useId
} from "@floating-ui/react";
import { useRef } from "react";

interface PopoverOptions {
    initialOpen?: boolean;
    placement?: Placement;
    modal?: boolean;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
}

export function usePopover({
    initialOpen = false,
    placement = "bottom-end",
    modal,
    open: controlledOpen,
    onOpenChange: setControlledOpen
}: PopoverOptions = {}) {
    const [uncontrolledOpen, setUncontrolledOpen] = React.useState(initialOpen);
    const [labelId, setLabelId] = React.useState<string | undefined>();
    const [descriptionId, setDescriptionId] = React.useState<
        string | undefined
    >();

    const open = controlledOpen ?? uncontrolledOpen;
    const setOpen = setControlledOpen ?? setUncontrolledOpen;
    const arrowRef = useRef(null);

    const data = useFloating({
        placement,
        open,
        onOpenChange: setOpen,
        whileElementsMounted: autoUpdate,
        middleware: [
            offset(5),
            flip({
                crossAxis: placement.includes("-"),
                fallbackAxisSideDirection: "end",
                padding: 5
            }),
            shift({ padding: 5 }),
            arrow({
                element: arrowRef,
            }),
        ]
    });

    const context = data.context;

    const click = useClick(context, {
        enabled: controlledOpen == null
    });
    const dismiss = useDismiss(context);
    const role = useRole(context);

    const interactions = useInteractions([click, dismiss, role]);

    return React.useMemo(
        () => ({
            open,
            setOpen,
            ...interactions,
            ...data,
            modal,
            labelId,
            descriptionId,
            setLabelId,
            setDescriptionId,
            arrowRef
        }),
        [open, setOpen, interactions, data, modal, labelId, descriptionId]
    );
}

type ContextType =
    | (ReturnType<typeof usePopover> & {
        setLabelId: React.Dispatch<React.SetStateAction<string | undefined>>;
        setDescriptionId: React.Dispatch<
            React.SetStateAction<string | undefined>
        >;
    })
    | null;

const PopoverContext = React.createContext<ContextType>(null);

export const usePopoverContext = () => {
    const context = React.useContext(PopoverContext);

    if (context == null) {
        throw new Error("Popover components must be wrapped in <Popover />");
    }

    return context;
};

export function Popover({
    children,
    modal = false,
    ...restOptions
}: {
    children: React.ReactNode;
} & PopoverOptions) {
    // This can accept any props as options, e.g. `placement`,
    // or other positioning options.
    const popover = usePopover({ modal, ...restOptions });
    return (
        <PopoverContext.Provider value={popover}>
            {children}
        </PopoverContext.Provider>
    );
}

interface PopoverTriggerProps {
    children: React.ReactNode;
    asChild?: boolean;
    onStateChange?: (state: boolean) => void;
}

export const PopoverTrigger = React.forwardRef<
    HTMLElement,
    React.HTMLProps<HTMLElement> & PopoverTriggerProps
>(function PopoverTrigger({ children, asChild = false, ...props }, propRef) {
    const context = usePopoverContext();
    const childrenRef = (children as any).ref;
    const ref = useMergeRefs([context.refs.setReference, propRef, childrenRef]);

    // `asChild` allows the user to pass any element as the anchor
    if (asChild && React.isValidElement(children)) {
        return React.cloneElement(
            children,
            context.getReferenceProps({
                ref,
                ...props,
                ...children.props,
                "data-state": context.open ? "open" : "closed"
            })
        );
    }

    React.useEffect(() => {
        props.onStateChange && props.onStateChange(context.open)
    }, [context.open])

    return (
        <button
            ref={ref}
            type="button"
            className="!emf-border-none !emf-border-0 !emf-bg-transparent !emf-p-0 !emf-m-0"
            // The user can style the trigger based on the state
            data-state={context.open ? "open" : "closed"}
            {...context.getReferenceProps(props)}
        >
            {children}
        </button>
    );
});

export const PopoverContent = React.forwardRef<
    HTMLDivElement,
    React.HTMLProps<HTMLDivElement>
>(function PopoverContent({ style, ...props }, propRef) {
    const { context: floatingContext, ...context } = usePopoverContext();
    const ref = useMergeRefs([context.refs.setFloating, propRef]);

    if (!floatingContext.open) return null;

    return (
        <FloatingPortal>
            <FloatingFocusManager context={floatingContext} modal={context.modal}>
                <div
                    ref={ref}
                    style={{ ...context.floatingStyles, ...style }}
                    aria-labelledby={context.labelId}
                    aria-describedby={context.descriptionId}
                    {...context.getFloatingProps(props)}
                >
                    {props.children}
                    <div
                        ref={context.arrowRef}
                        className="emf-widget-popover-arrow"
                        style={{
                            position: 'absolute',
                            width: '12px',
                            height: '12px',
                            background: 'white',
                            boxShadow: '-0.6px -0.6px 0px #00000029',
                            transform: 'rotate(45deg)',
                            ...getArrowStyles(context.middlewareData.arrow, context.placement),
                        }}
                    />

                </div>
            </FloatingFocusManager>
        </FloatingPortal>
    );
});

export const PopoverClose = React.forwardRef<
    HTMLButtonElement,
    React.ButtonHTMLAttributes<HTMLButtonElement>
>(function PopoverClose(props, ref) {
    const { setOpen } = usePopoverContext();
    return (
        <button
            type="button"
            ref={ref}
            {...props}
            onClick={(event) => {
                props.onClick?.(event);
                setOpen(false);
            }}
        />
    );
});



const getArrowStyles = (arrowData, placement) => {
    if (!arrowData) return {};

    const { x, y } = arrowData;
    const basePlacement = placement.split('-')[0];
    const staticSide = {
        top: 'bottom',
        right: 'left',
        bottom: 'top',
        left: 'right',
    }[basePlacement];


    const rotation = {
        top: 'rotate(225deg)',     // arrow pointing down
        bottom: 'rotate(45deg)',   // arrow pointing up
        left: 'rotate(135deg)',    // arrow pointing right
        right: 'rotate(-45deg)',   // arrow pointing left
    }[basePlacement];


    return {
        left: x != null ? `${x}px` : '',
        top: y != null ? `${y}px` : '',
        [staticSide]: '-5px',
        transform: rotation,
    };
}

